import {Injectable} from '@angular/core';
import {SettingTypeEnum} from './setting-type.enum';
import {Setting} from './setting';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {SettingsApiService} from './settings-api.service';
import {Project} from '@mydocma/project';
import {SettingNamesEnum} from './setting-names.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {executeWithTimeout} from '@docma-mm-core/rx/rx-functions';
import {ValueTypeEnum} from './value-type.enum';
import {firstValueFrom} from 'rxjs';
import {CustomerService} from '@mydocma/customer';
import {SettingModuleEnum} from "./setting-module.enum";

const dbgPrint = false;
const dbgPrint_Settings = dbgPrint && true;

@Injectable()
export class SettingsService {
  constructor(private readonly settingsApiService: SettingsApiService,
              private readonly personalizationService: PersonalizationService,
              private readonly customerService: CustomerService) {
  }

  async save(
    value: string,
    keyIDB: PersonalizationKeysEnum,
    keySettings: SettingNamesEnum,
    project: Project,
    moduleName?: SettingModuleEnum,
    opts?: { scope?: SettingTypeEnum, throwOnError?: boolean }) {
    const valueObj = {value, modifiedOn: Date.now()};
    const type = opts?.scope ?? (project ? SettingTypeEnum.userproject : SettingTypeEnum.user);
    const throwOnError = opts?.throwOnError ?? false;

    if (throwOnError) {
      const foundSetting = await executeWithTimeout<Setting>(this.settingsApiService.get(type, keySettings, moduleName, throwOnError), `Loading of ${type} setting '${keySettings}' timed out!`, 3000);
      if (!foundSetting?.id) {
        await executeWithTimeout<Setting>(this.settingsApiService.create(type, keySettings, JSON.stringify(valueObj), moduleName, throwOnError), `Creating ${type} setting '${keySettings}' timed out!`, 3000);
      } else {
        await executeWithTimeout<Setting>(this.settingsApiService.update(type, keySettings, JSON.stringify(valueObj), moduleName, throwOnError), `Updating ${type} setting '${keySettings}' timed out!`, 3000);
      }
    } else {
      // TODO: fire-and-forget legacy path. Replace with awaited path above for all callers in a follow-up
      //  (avoided here to limit blast radius before production release).
      executeWithTimeout<Setting>(this.settingsApiService.get(type, keySettings, moduleName), `Loading of ${type} setting '${keySettings}' timed out!`, 3000)
        .then(foundSetting => {
          if (!foundSetting?.id) {
            executeWithTimeout<Setting>(this.settingsApiService.create(type, keySettings, JSON.stringify(valueObj), moduleName), `Creating ${type} setting '${keySettings}' timed out!`, 3000);
          } else {
            executeWithTimeout<Setting>(this.settingsApiService.update(type, keySettings, JSON.stringify(valueObj), moduleName), `Updating ${type} setting '${keySettings}' timed out!`, 3000);
          }
        });
    }

    if (project) {
      await this.personalizationService.setProjectValue(project.id, keyIDB, JSON.stringify(valueObj));
    } else {
      await this.personalizationService.setValue(keyIDB, JSON.stringify(valueObj));
    }
  }

  async delete(
    keyIDB: PersonalizationKeysEnum,
    keySettings: SettingNamesEnum,
    project: Project,
    moduleName?: SettingModuleEnum,
    opts?: { scope?: SettingTypeEnum, throwOnError?: boolean }) {
    const type = opts?.scope ?? (project ? SettingTypeEnum.userproject : SettingTypeEnum.user);
    const throwOnError = opts?.throwOnError ?? false;
    await this.settingsApiService.delete(type, keySettings, moduleName, throwOnError);
    await this.personalizationService.deleteValue((project ? `${project.id}.${keyIDB}` : keyIDB));
  }

  async deleteAll(
    keySettings: SettingNamesEnum,
    project: Project,
    moduleName?: SettingModuleEnum,
    opts?: { scope?: SettingTypeEnum }) {
    const type = opts?.scope ?? (project ? SettingTypeEnum.userproject : SettingTypeEnum.user);
    await this.settingsApiService.deleteAll(type, keySettings, moduleName);
  }


  async load(
    keyIDB: PersonalizationKeysEnum,
    keySettings: SettingNamesEnum,
    project: Project,
    moduleName?: SettingModuleEnum,
    opts?: { scope?: SettingTypeEnum }
  ) {

    let valueIDB: any;
    let valueSettings: Setting;
    const type = opts?.scope ?? (project ? SettingTypeEnum.userproject : SettingTypeEnum.user);

    if (project) {
      valueIDB = await this.personalizationService.getProjectValue(project.id, keyIDB);
    } else {
      valueIDB = await this.personalizationService.getValue(keyIDB);
    }

    valueSettings = await executeWithTimeout<Setting>(this.settingsApiService.get(type, keySettings, moduleName),
      `Loading of ${type} setting '${keySettings}' timed out!`,
      3000);

    const value1 = (valueIDB && valueIDB !== '' && !(valueIDB instanceof Object && Object.keys(valueIDB).length === 0) ? JSON.parse(valueIDB) : null);
    const value2 = (valueSettings && valueSettings.value !== '' && !(valueSettings instanceof Object && Object.keys(valueSettings?.value).length === 0) ? JSON.parse(valueSettings.value) : null);

    const storeIDBFunc = async (value) => (project ?
      await this.personalizationService.setProjectValue(project.id, keyIDB, value) :
      await this.personalizationService.setValue(keyIDB, value));
    const storeSettingFunc = async (value) => (valueSettings?.id ?
      executeWithTimeout<Setting>(this.settingsApiService.update(type, keySettings, value, moduleName), `Updating ${type} setting '${keySettings}' timed out!`, 3000) :
      executeWithTimeout<Setting>(this.settingsApiService.create(type, keySettings, value, moduleName), `Creating ${type} setting '${keySettings}' timed out!`, 3000));

    // , `Creating user setting '${keySettings}' timed out!`, 5000)
    const d1: Date = value1?.modifiedOn;
    const d2: Date = value2?.modifiedOn;

    if (d1 && d2) {
      if (d1 > d2) {
        // take d1 & store to d2
        await storeSettingFunc(value1);
        return value1.value;
      } else if (d1 < d2) {
        // take d2 & store to d1
        await storeIDBFunc(JSON.stringify(value2));
        return value2.value;
      } else {
        // take d1
        return value1.value;
      }
    } else if (!d1 && d2) {
      // take d2 & store to d1
      await storeIDBFunc(JSON.stringify(value2));
      return value2.value;
    } else if (d1 && !d2) {
      // take d1 & store to d2
      await storeSettingFunc(JSON.stringify(value1));
      return value1.value;
    } else {
      // NO modifiedOn existing...
      if (value1) {
        // take d1 & store to d1 + d2
        const value = JSON.stringify({value: value1, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value1;
      } else if (value2) {
        // take d2 & store to d1 + d2
        const value = JSON.stringify({value: value2, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value2;
      } else {
        // NO VALUE EXISTING!
        return null;
      }
    }
  }

  async loadGlobal(keyIDB: PersonalizationKeysEnum, keySettings: SettingNamesEnum, saveToSettingsApi: boolean = true, moduleName?: SettingModuleEnum) {
    let valueIDB: any;
    let valueSettings: Setting;

    const customer = await firstValueFrom(this.customerService.currentCustomer);

    const customerKeyIDB = `${customer.id}.${keyIDB}`;

    valueIDB = await this.personalizationService.getValue(customerKeyIDB);
    valueSettings = await executeWithTimeout<Setting>(this.settingsApiService.get(SettingTypeEnum.global, keySettings, moduleName),
      `Loading of global setting '${keySettings}' timed out!`,
      3000);

    const value1 = (valueIDB && valueIDB !== '' && !(valueIDB instanceof Object && Object.keys(valueIDB).length === 0) ? JSON.parse(valueIDB) :
      (valueIDB instanceof Boolean ? valueSettings.value : null));

    const value2 = (valueSettings && valueSettings.value !== '' && !(valueSettings instanceof Object && Object.keys(valueSettings?.value).length === 0) ? JSON.parse(valueSettings.value) :
      (valueSettings?.valueType === ValueTypeEnum.boolean ? valueSettings.value : null));

    const storeIDBFunc = async (value) => {
      if (!saveToSettingsApi && valueSettings === null) {
        await this.personalizationService.deleteValue(customerKeyIDB);
      } else {
        await this.personalizationService.setValue(customerKeyIDB, value);
      }
    };

    const storeSettingFunc = async (value) => {
      if (valueSettings?.id && saveToSettingsApi === true) {
        executeWithTimeout<Setting>(this.settingsApiService.update(SettingTypeEnum.global, keySettings, value, moduleName), `Creating global setting '${keySettings}' timed out!`, 3000);
      } else if (valueSettings == null && saveToSettingsApi === true) {
        executeWithTimeout<Setting>(this.settingsApiService.create(SettingTypeEnum.global, keySettings, value, moduleName), `Updating global setting '${keySettings}' timed out!`, 3000)
      }
    };

    // , `Creating user setting '${keySettings}' timed out!`, 5000)
    const d1: Date = value1?.modifiedOn;
    const d2: Date = value2?.modifiedOn;

    if (d1 && d2) {
      if (d1 > d2) {
        // take d1 & store to d2
        await storeSettingFunc(value1);
        return value1.value;
      } else if (d1 < d2) {
        // take d2 & store to d1
        await storeIDBFunc(JSON.stringify(value2));
        return value2.value;
      } else {
        // take d1
        return value1.value;
      }
    } else if (!d1 && d2) {
      // take d2 & store to d1
      await storeIDBFunc(JSON.stringify(value2));
      return value2.value;
    } else if (d1 && !d2 && saveToSettingsApi === true) {
      // take d1 & store to d2
      await storeSettingFunc(JSON.stringify(value1));
      return value1.value;
    } else if (d1 && !d2 && saveToSettingsApi === false) {
      // take d1 & store to d2
      await storeIDBFunc(JSON.stringify(value2));
      return value2;
    } else {
      // NO modifiedOn existing...
      if (value1) {
        // take d1 & store to d1 + d2
        const value = JSON.stringify({value: value1, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value1;
      } else if (value2) {
        // take d2 & store to d1 + d2
        const value = JSON.stringify({value: value2, modifiedOn: Date.now()});
        await storeIDBFunc(value);
        await storeSettingFunc(value);
        return value2;
      } else {
        // NO VALUE EXISTING!
        return null;
      }
    }
  }
}


export async function GetValue<T>(value: any, defaultValue?: T) {
  if (value && value != '' && !(value instanceof Object && Object.keys(value).length === 0)) {
    let parsedValue: T;

    try {
      if (dbgPrint_Settings) {
        console.warn('value');
        console.warn(value);
      }

      parsedValue = JSON.parse(value);

      if (dbgPrint_Settings) {
        console.warn('parsedValue');
        console.warn(parsedValue);
      }

      return parsedValue;
    } catch (err) {
      if (dbgPrint_Settings) {
        console.warn('err');
        console.warn(err);
      }
      return defaultValue;
    }
  } else {
    return defaultValue;
  }
}


